
import React, { useState, useEffect, useRef } from "react";
import Layout from "./Layout";
import {
  FaSpinner,
  FaExclamationTriangle,
  FaExpandAlt,
  FaCompressAlt,
  FaArrowLeft,
  FaExternalLinkAlt,
  FaGlobe,
  FaFlag,
  FaReadme,
  FaPlus,
  FaDownload,
  FaComments,
  FaClock,
  FaRedo,
  FaCalendarAlt,
  FaCalculator, // Specific to Fog Index
  FaRulerCombined,
  FaLayerGroup,
  FaBookOpen,
  FaStar,
  FaEdit,
  FaFingerprint,
  FaChartLine,
  FaInfoCircle,
  FaChevronRight,
  FaFileAlt,
  FaMagic, // Added for the Generate button
} from "react-icons/fa";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./FogIndex.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function FogIndex({ embedded = false, miniShotId = null }) {
  const [miniShots, setMiniShots] = useState([]);
  const [selectedMiniShot, setSelectedMiniShot] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [expandedReportFields, setExpandedReportFields] = useState(new Set());
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const [isDirectView] = useState(() => {
    if (embedded) return true;
    const skipSelection = sessionStorage.getItem("skipSelection");
    const storedMiniShot = sessionStorage.getItem("selectedMiniShot");
    const urlParams = new URLSearchParams(window.location.search);
    const fromChat = urlParams.get("from");
    return (
      skipSelection === "true" && storedMiniShot !== null && fromChat === "chat"
    );
  });

  const hasInitializedRef = useRef(false);
  const reportRef = useRef();

  useEffect(() => {
    if (embedded && miniShotId) {
      setSelectedMiniShot({ id: miniShotId, title: "Report Analysis" });
      loadReportForMiniShot(miniShotId);
      return;
    }

    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const skipSelection = sessionStorage.getItem("skipSelection");
    const storedMiniShot = sessionStorage.getItem("selectedMiniShot");
    const urlParams = new URLSearchParams(window.location.search);
    const fromChat = urlParams.get("from");

    if (skipSelection === "true" && storedMiniShot && fromChat === "chat") {
      const miniShot = JSON.parse(storedMiniShot);
      setSelectedMiniShot(miniShot);
      setIsLoading(false);
      loadReportForMiniShot(miniShot.id);
    } else {
      sessionStorage.removeItem("skipSelection");
      sessionStorage.removeItem("selectedMiniShot");
      loadMiniShots();
    }
  }, [embedded, miniShotId]);

  useEffect(() => {
    if (reportData && reportData.reportData) {
      if (reportData.reportData.sections) {
        const allSectionIndices = reportData.reportData.sections.map(
          (_, index) => index
        );
        setExpandedSections(new Set(allSectionIndices));
      }
      const reportLevelFields = Object.keys(reportData.reportData).map(
        (_, index) => index
      );
      setExpandedReportFields(new Set(reportLevelFields));
    }
  }, [reportData]);

  const handleGenerateReport = async (idToUse = null) => {
    const targetId = idToUse || (selectedMiniShot ? selectedMiniShot.id : null);
    if (!targetId) return;

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/test-reports/generate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            miniShotId: targetId,
            engineType: "fog_index",
          }),
        }
      );

      if (!response.ok) throw new Error("Generation failed");

      const data = await response.json();
      if (data.success) {
        await loadReportForMiniShot(targetId);
      } else {
        throw new Error(data.error || "Generation failed");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const loadMiniShots = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/api/test-reports/minishots`,
        {
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) throw new Error("Failed to load MiniShots");
      const data = await response.json();
      if (data.success && data.miniShots) {
        setMiniShots(data.miniShots);
      } else {
        throw new Error(data.error || "Failed to load MiniShots");
      }
    } catch (error) {
      setError(error.message);
      setMiniShots([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadReportForMiniShot = async (miniShotId) => {
    try {
      setIsLoadingReport(true);
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/api/test-reports?miniShotId=${miniShotId}&reportType=fog_index&limit=1`,
        {
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) throw new Error("Failed to load reports");
      const data = await response.json();

      if (data.success && data.reports && data.reports.length > 0) {
        const reportSummary = data.reports[0];
        const fullReportResponse = await fetch(
          `${API_BASE_URL}/api/test-reports/${reportSummary.id}`,
          {
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          }
        );
        if (fullReportResponse.ok) {
          const fullReportData = await fullReportResponse.json();
          if (fullReportData.success && fullReportData.report) {
            setReportData(fullReportData.report);
          } else {
            setReportData(null);
          }
        } else {
          setReportData(null);
        }
      } else {
        setReportData(null);
        if (embedded) {
          await handleGenerateReport(miniShotId);
        }
      }
    } catch (error) {
      console.error("Failed to load Fog Index report:", error);
      setError(error.message);
      setReportData(null);
    } finally {
      setIsLoadingReport(false);
      if (embedded) setIsLoading(false);
    }
  };

  const handleMiniShotSelect = (miniShot) => {
    setSelectedMiniShot(miniShot);
    setReportData(null);
    loadReportForMiniShot(miniShot.id);
  };

  const handleBackToProjects = () => {
    if (embedded) return;
    if (isDirectView) {
      window.history.back();
      return;
    }
    setSelectedMiniShot(null);
    setReportData(null);
    setError(null);
    setExpandedSections(new Set());
    setExpandedReportFields(new Set());
    sessionStorage.removeItem("selectedMiniShot");
    sessionStorage.removeItem("skipSelection");
  };

  const toggleSection = (index) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(index)) newExpanded.delete(index);
    else newExpanded.add(index);
    setExpandedSections(newExpanded);
  };

  const toggleReportField = (index) => {
    const newExpanded = new Set(expandedReportFields);
    if (newExpanded.has(index)) newExpanded.delete(index);
    else newExpanded.add(index);
    setExpandedReportFields(newExpanded);
  };

  const getScoreColor = (score) => {
    const percentage = (score / 100) * 100;
    if (percentage >= 80) return "#10b981";
    if (percentage >= 60) return "#f59e0b";
    if (percentage >= 40) return "#f97316";
    return "#ef4444";
  };

  const getReadinessClass = (readiness) => {
    switch (readiness) {
      case "Excellent":
        return "badge-excellent";
      case "Good":
        return "badge-good";
      case "Basic":
        return "badge-basic";
      default:
        return "badge-default";
    }
  };

  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .trim();
  };

  // Icons specific to Fog Index
  const getSectionIcon = (index) => {
    const icons = [
      <FaCalculator />,
      <FaBookOpen />,
      <FaRulerCombined />,
      <FaLayerGroup />,
      <FaEdit />,
      <FaStar />,
      <FaChartLine />,
      <FaFingerprint />,
    ];
    return icons[index % icons.length] || <FaInfoCircle />;
  };

  const renderDataField = (data, depth = 0, fieldKey = "") => {
    if (!data && data !== 0 && data !== false)
      return <span className="doc-no-data">N/A</span>;

    if (typeof data === "string") {
      if (data.startsWith("http"))
        return (
          <a
            href={data}
            target="_blank"
            rel="noopener noreferrer"
            className="doc-link"
          >
            <FaExternalLinkAlt /> {data}
          </a>
        );
      return <div className="doc-text">{data}</div>;
    }

    if (typeof data === "number")
      return <span className="doc-number">{data.toLocaleString()}</span>;
    if (typeof data === "boolean")
      return (
        <span className={`doc-boolean ${data ? "true" : "false"}`}>
          {data ? "Yes" : "No"}
        </span>
      );

    if (Array.isArray(data)) {
      if (data.length === 0)
        return <span className="doc-no-data">Empty list</span>;

      if (
        data.some(
          (item) => item && typeof item === "object" && (item.url || item.link)
        )
      ) {
        return (
          <div className="doc-sources-list">
            {data.map((item, idx) => (
              <div key={idx} className="doc-source-item">
                <div className="doc-source-icon">
                  <FaGlobe />
                </div>
                <div className="doc-source-content">
                  {Object.entries(item)
                    .filter(([key]) => key.toLowerCase() !== "query")
                    .map(([key, value]) => (
                      <div key={key} className="doc-nested-row">
                        <strong>{formatFieldName(key)}:</strong>{" "}
                        {renderDataField(value, depth + 1, key)}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        );
      }

      return (
        <ul className="doc-list">
          {data.map((item, idx) => (
            <li key={idx} className="doc-list-item">
              {renderDataField(item, depth + 1, fieldKey)}
            </li>
          ))}
        </ul>
      );
    }

    if (typeof data === "object") {
      const entries = Object.entries(data);
      if (entries.length === 0) return <span className="doc-no-data">-</span>;

      return (
        <div className={`doc-object ${depth > 0 ? "nested" : ""}`}>
          {entries.map(([key, value]) => (
            <div key={key} className="doc-field-row">
              <div className="doc-field-label">{formatFieldName(key)}</div>
              <div className="doc-field-value">
                {renderDataField(value, depth + 1, key)}
              </div>
            </div>
          ))}
        </div>
      );
    }
    return <span>{String(data)}</span>;
  };

  const renderReportSection = (section, index) => {
    if (!section) return null;
    const isExpanded = expandedSections.has(index);
    return (
      <div key={index} className="document-section">
        <div
          className="doc-section-header"
          onClick={() => toggleSection(index)}
        >
          <div className="doc-section-title-group">
            <span className="doc-section-icon">{getSectionIcon(index)}</span>
            <div className="doc-section-info">
              <h3>{section.title || `Section ${index + 1}`}</h3>
              {section.description && (
                <span className="doc-section-desc">{section.description}</span>
              )}
            </div>
          </div>
          <div className="doc-toggle-btn">
            {isExpanded ? <FaCompressAlt /> : <FaExpandAlt />}
          </div>
        </div>

        {isExpanded && (
          <div className="doc-section-body">
            {Object.entries(section)
              .filter(
                ([key]) =>
                  key.toLowerCase() !== "score" &&
                  key !== "title" &&
                  key !== "description"
              )
              .map(([fieldKey, fieldValue]) => (
                <div key={fieldKey} className="doc-content-block">
                  <h4>{formatFieldName(fieldKey)}</h4>
                  <div className="doc-block-content">
                    {renderDataField(fieldValue, 0, fieldKey)}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  };

  const renderDynamicReportFields = () => {
    if (!reportData?.reportData) return null;
    const excludedFields = [
      "sections",
      "reportId",
      "title",
      "reportType",
      "generatedAt",
      "generatedBy",
      "overallScore",
      "conclusion",
      "webSources",
    ];
    const reportFields = Object.entries(reportData.reportData).filter(
      ([key]) => !excludedFields.includes(key)
    );
    if (reportFields.length === 0) return null;

    return (
      <div className="document-section dynamic-fields-section">
        <div className="doc-section-header">
          <h3>Readability Metrics</h3>
        </div>
        <div className="doc-section-body">
          {reportFields.map(([fieldKey, fieldValue], index) => {
            const isExpanded = expandedReportFields.has(index);
            return (
              <div key={fieldKey} className="doc-sub-section">
                <div
                  className="doc-sub-header"
                  onClick={() => toggleReportField(index)}
                >
                  <span>{formatFieldName(fieldKey)}</span>
                  {isExpanded ? (
                    <FaCompressAlt size={12} />
                  ) : (
                    <FaExpandAlt size={12} />
                  )}
                </div>
                {isExpanded && (
                  <div className="doc-sub-content">
                    {renderDataField(fieldValue, 0, fieldKey)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderConclusion = () => {
    if (!reportData?.reportData?.conclusion) return null;
    return (
      <div className="document-section conclusion">
        <div className="doc-section-header no-border">
          <h3>
            <FaFlag style={{ marginRight: "8px", color: "#dc2626" }} /> Final
            Assessment
          </h3>
        </div>
        <div className="doc-section-body">
          <div className="doc-conclusion-text">
            {reportData.reportData.conclusion}
          </div>
        </div>
      </div>
    );
  };

  const renderWebSources = () => {
    if (
      !reportData?.reportData?.webSources ||
      !Array.isArray(reportData.reportData.webSources) ||
      reportData.reportData.webSources.length === 0
    )
      return null;
    return (
      <div className="document-section">
        <div className="doc-section-header">
          <h3>Research References</h3>
        </div>
        <div className="doc-section-body">
          <div className="doc-sources-grid">
            {reportData.reportData.webSources.map((source, index) => (
              <div key={index} className="doc-source-mini">
                <FaGlobe className="source-icon" />
                <div className="source-data">
                  {Object.entries(source)
                    .filter(([key]) => key.toLowerCase() !== "query")
                    .map(([key, value]) => (
                      <div key={key}>
                        <strong>{formatFieldName(key)}:</strong>{" "}
                        {renderDataField(value)}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const downloadReportAsPDF = async () => {
    if (!reportRef.current) return;
    setIsGeneratingPDF(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        windowHeight: reportRef.current.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          0,
          -(imgHeight - heightLeft),
          pdfWidth,
          imgHeight
        );
        heightLeft -= pdfHeight;
      }

      const filename = `${selectedMiniShot.title.replace(
        /[^a-zA-Z0-9]/g,
        "_"
      )}_Fog_Report.pdf`;
      pdf.save(filename);
    } catch (error) {
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Logic to determine classes for main wrapper
  const containerClasses = `fog-index-container ${
    embedded ? "embedded-mode" : ""
  } ${selectedMiniShot ? "mode-document" : "mode-selection"}`;

  const mainContent = (
    <div className={containerClasses}>
      {!selectedMiniShot && !isLoading && !isGenerating ? (
        // --- SELECTION MODE (WHITE BACKGROUND) ---
        <div className="project-selection-wrapper">
          <div className="simple-header">
            <h1>Fog Index</h1>
            <p>
              Select a project to view its readability & complexity analysis
            </p>
          </div>
          {error && (
            <div className="error-banner">
              <FaExclamationTriangle />
              <span>{error}</span>
              <button onClick={loadMiniShots}>Retry</button>
            </div>
          )}

          <div className="projects-selection-container">
            {miniShots.length === 0 ? (
              <div className="empty-state-full">
                <div className="empty-icon-wrap">
                  <FaReadme className="empty-icon" />
                </div>
                <h2>No Projects Found</h2>
                <p>Start a new conversation to generate fog index reports.</p>
                <button
                  className="create-btn"
                  onClick={() => (window.location.href = "/moonshot")}
                >
                  <FaPlus />
                  Create New Project
                </button>
              </div>
            ) : (
              <div className="projects-grid">
                {miniShots.map((miniShot) => (
                  <div
                    key={miniShot.id}
                    className="project-card-modern"
                    onClick={() => handleMiniShotSelect(miniShot)}
                  >
                    <div className="card-top">
                      <div className="card-header-row">
                        <span className="card-date">
                          {new Date(miniShot.createdAt).toLocaleDateString(
                            undefined,
                            { month: "short", day: "numeric", year: "numeric" }
                          )}
                        </span>
                        <div
                          className={`status-pill ${getReadinessClass(
                            miniShot.reportReadiness
                          )}`}
                        >
                          {miniShot.reportReadiness || "Basic"}
                        </div>
                      </div>
                      <h3 className="card-title">{miniShot.title}</h3>
                      <p className="card-desc">
                        {miniShot.description ||
                          "No description available for this analysis."}
                      </p>
                    </div>

                    <div className="card-bottom">
                      <div className="meta-stats">
                        <span>
                          <FaComments /> {miniShot.messageCount || 0} msgs
                        </span>
                      </div>
                      <button className="card-action-btn">
                        View Report <FaChevronRight size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        // --- REPORT MODE (GREY BACKGROUND) ---
        <div className="document-wrapper">
          {/* MODERN TOOLBAR */}
          <div className="document-toolbar">
            <div className="toolbar-left">
              {!embedded && (
                <button
                  className="modern-back-btn"
                  onClick={handleBackToProjects}
                >
                  <FaArrowLeft />
                </button>
              )}
              <div className="toolbar-info">
                <span className="toolbar-label">Fog Index Report</span>
                <span className="toolbar-title">{selectedMiniShot?.title}</span>
              </div>
            </div>
            <div className="toolbar-actions">
              <button
                className="toolbar-btn primary"
                onClick={downloadReportAsPDF}
                disabled={isGeneratingPDF || !reportData}
              >
                <FaDownload /> {isGeneratingPDF ? "Saving..." : "Download PDF"}
              </button>
              <button
                className="toolbar-btn secondary"
                onClick={() => handleGenerateReport()}
                disabled={isGenerating}
              >
                <FaRedo className={isGenerating ? "spin" : ""} />{" "}
                {isGenerating ? "Analyzing..." : "Regenerate"}
              </button>
            </div>
          </div>

          <div className="document-scroll-area">
            {isLoadingReport || isGenerating ? (
              <div className="loading-paper">
                <FaSpinner className="spinner" />
                <h3>Calculating Fog Index...</h3>
                <p>AI is analyzing text complexity and readability.</p>
              </div>
            ) : !reportData ? (
              // UPDATED EMPTY STATE FOR REPORT
              <div className="report-not-ready-card">
                <div className="not-ready-icon-container">
                  <FaFileAlt className="not-ready-icon" />
                </div>
                <h3>Report Not Available</h3>
                <p>
                  A Fog Index report hasn't been generated for this project yet.
                </p>
                <button
                  className="generate-now-btn"
                  onClick={() => handleGenerateReport()}
                >
                  <FaMagic /> Generate Analysis
                </button>
              </div>
            ) : (
              <div className="document-paper" ref={reportRef}>
                <div className="paper-header">
                  <div className="paper-brand">MOONSHOT AI AUDIT</div>
                  <div className="paper-title-row">
                    <h1>Fog Index Analysis Report</h1>
                    <div
                      className="paper-score-box"
                      style={{
                        borderColor: getScoreColor(reportData.overallScore),
                      }}
                    >
                      <span className="score-label">FOG SCORE</span>
                      <span
                        className="score-val"
                        style={{
                          color: getScoreColor(reportData.overallScore),
                        }}
                      >
                        {reportData.overallScore}
                      </span>
                    </div>
                  </div>
                  <div className="paper-meta-row">
                    <div className="meta-group">
                      <span className="meta-label">PROJECT TITLE</span>
                      <span className="meta-value">
                        {selectedMiniShot?.title}
                      </span>
                    </div>
                    <div className="meta-group">
                      <span className="meta-label">GENERATED ON</span>
                      <span className="meta-value">
                        <FaCalendarAlt />{" "}
                        {new Date(reportData.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="meta-group">
                      <span className="meta-label">AUDIT ID</span>
                      <span className="meta-value">
                        #
                        {reportData.id
                          ? reportData.id.slice(-6).toUpperCase()
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                  <hr className="paper-divider" />
                </div>

                <div className="paper-body">
                  {renderDynamicReportFields()}

                  {reportData.reportData?.sections && (
                    <div className="paper-analysis-sections">
                      <h2 className="section-heading-main">
                        Complexity Analysis
                      </h2>
                      {reportData.reportData.sections.map((section, index) =>
                        renderReportSection(section, index)
                      )}
                    </div>
                  )}

                  {renderConclusion()}
                  {renderWebSources()}
                </div>

                <div className="paper-footer">
                  <span>Moonshot AI • Fog Index • Confidential</span>
                  <span>Page 1 of 1</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) return mainContent;
  return isDirectView ? mainContent : <Layout>{mainContent}</Layout>;
}
